import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './styles.module.scss';
import classNames from 'classnames/bind';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiGiftCardsService } from '@api';
import { EGiftCardStatus, EUserType, TGetGiftCardsFilter, TGiftCardListItem } from '@xeppt/xeppt-sdk/types';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import Typography from '@components/common/typography';
import { Icon } from '@components/icons';
import Info from '@components/common/info';
import Status from '@components/common/status';
import Button from '@components/common/button';
import Skeleton from '@components/common/skeleton';
import { useUserContext } from '@hooks/context/useUserContext';
import { useEnvironmentContext } from '@hooks/context/useEnvironmentContext';
import { useGiftCardsModals } from '@hooks/modals/useGiftCardsModals';
import { getGiftCardStatusVariant, prettifyAmount } from '@utils/index';
import { getGiftCardStatusesList } from '@utils/billing';
import { ENewNotificationType } from '@enum';
import DevPageHeader from '../_shared/PageHeader';

const cx = classNames.bind(styles);

const GC_PAGE_SIZE = 30;

// TGetGiftCardsFilter (SDK) is a `type` alias, not an `interface` —
// TypeScript module augmentation (`declare module`) can only merge into
// `interface` declarations, so unlike orders/sales' TGetOrdersRequest/
// TGetSalesRequest, this field can't be added that way (see
// src/api/order_environment_augment.d.ts for the interface case). The
// backend's gift_cards_list.go already accepts/filters by `environment`
// (same as orders/sales) — this local request type + cast sends it without
// an SDK release.
type TGetGiftCardsFilterWithEnv = TGetGiftCardsFilter & { environment?: 'test' | 'live' };

const DevelopersGiftCardsLayout = () => {
    const { user } = useUserContext();
    const { env } = useEnvironmentContext();
    const [cards, setCards] = useState<TGiftCardListItem[]>([]);
    const [page, setPage] = useState(1);
    const [trigger, setTrigger] = useState(0);
    const loader = useRef(null);

    const {
        data: listResponse,
        isLoading,
        error,
        handleRequest: fetchCards
    } = useApiQuery({
        method: (req?: { isNew?: boolean; page?: number }) => {
            const targetPage = req?.isNew ? 1 : (req?.page ?? 1);
            return apiGiftCardsService.getGiftCards({
                page: targetPage,
                page_size: GC_PAGE_SIZE,
                order: 'DESC',
                order_field: 'created_at',
                environment: env
            } as TGetGiftCardsFilterWithEnv);
        },
        onSuccess: (data) => {
            if (data.page !== 1) {
                setCards((prev) => [...prev, ...data.data]);
            } else {
                setCards(data.data ?? []);
            }
            setPage(data.page);
        },
        notificationType: ENewNotificationType.ACCOUNT,
        condition: user?.type === EUserType.BUSINESS,
        deps: [user, env]
    });

    const { handleOpenViewGiftCardModal } = useGiftCardsModals({
        refetchGiftCards: () => fetchCards({ isNew: true })
    });

    const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
        const target = entries[0];
        if (target.isIntersecting) {
            setTrigger((prev) => prev + 1);
        }
    }, []);

    useEffect(() => {
        const option = { root: null, rootMargin: '20px', threshold: 0 };
        const observer = new IntersectionObserver(handleObserver, option);
        if (loader.current) {
            observer.observe(loader.current);
        }
    }, [handleObserver]);

    useEffect(() => {
        if (cards.length < (listResponse?.total ?? 0) && !isLoading) {
            fetchCards({ isNew: false, page: page + 1 });
        }
    }, [trigger]);

    const totals = useMemo(
        () => [
            {
                title: 'Total issued',
                helper: 'Total number of gift cards issued',
                value: `${listResponse?.total ?? 0}`,
                style: styles.blue,
                icon: 'gift_card' as const
            },
            {
                title: 'Reserved balance',
                helper: 'Sum of face values across all active gift cards still outstanding',
                value: prettifyAmount((listResponse?.total_reserved_balance ?? 0).toFixed(2)),
                style: styles.pending,
                icon: 'money' as const
            },
            {
                title: 'Redeemed balance',
                helper: 'Total value redeemed by recipients across all gift cards',
                value: prettifyAmount((listResponse?.total_redeemed_balance ?? 0).toFixed(2)),
                style: styles.green,
                icon: 'check_outlined' as const
            }
        ],
        [listResponse]
    );

    const getStatusLabel = (status: EGiftCardStatus) =>
        getGiftCardStatusesList.find((item) => item.value === status)?.label ?? String(status);

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <DevPageHeader
                title="Gift cards"
                subtitle="Issue, track and manage gift cards from your XEPPT account."
            />

            <div className={styles.stats}>
                {isLoading && !cards.length ? (
                    <>
                        <Skeleton className={styles.stat_skeleton} />
                        <Skeleton className={styles.stat_skeleton} />
                        <Skeleton className={styles.stat_skeleton} />
                    </>
                ) : (
                    <>
                        {totals.map((item) => (
                            <div key={item.title} className={cx([styles.stat_card, item.style])}>
                                <div className={styles.stat_icon}>
                                    <Icon name={item.icon} width={22} height={22} />
                                </div>
                                <div className={styles.stat_body}>
                                    <div className={styles.stat_label_row}>
                                        <p className={styles.stat_label}>{item.title}</p>
                                        <Info variant="left" content={<>{item.helper}</>} />
                                    </div>
                                    <Typography
                                        variant="h5"
                                        weight="semibold"
                                        className={styles.stat_value}>
                                        {item.value}
                                    </Typography>
                                </div>
                            </div>
                        ))}
                    </>
                )}
            </div>

            <div className={styles.table_card}>
                <div className={styles.table_head}>
                    <Typography variant="body2" weight="semibold">
                        All gift cards
                    </Typography>
                </div>
                <div className={styles.table_wrap}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Serial number</th>
                                <th>Amount</th>
                                <th>Available</th>
                                <th>Recipient</th>
                                <th>Status</th>
                                <th>Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cards.map((card) => (
                                <tr
                                    key={card.id}
                                    className={styles.row}
                                    onClick={() => handleOpenViewGiftCardModal(card.id)}>
                                    <td className={styles.serial}>
                                        {card.gift_card_serial_number}
                                    </td>
                                    <td>${prettifyAmount((card.amount ?? 0).toFixed(2))}</td>
                                    <td>
                                        ${prettifyAmount((card.available_amount ?? 0).toFixed(2))}
                                    </td>
                                    <td className={styles.recipient}>
                                        {card.email ||
                                            (card.phone_code && card.phone_number
                                                ? `${card.phone_code.includes('+') ? card.phone_code : '+' + card.phone_code} ${card.phone_number}`
                                                : '-')}
                                    </td>
                                    <td>
                                        <Status variant={getGiftCardStatusVariant(card.status)}>
                                            {getStatusLabel(card.status)}
                                        </Status>
                                    </td>
                                    <td>
                                        {new Date(card.created_at).toLocaleDateString('en-CA', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                        })}
                                    </td>
                                </tr>
                            ))}
                            {isLoading &&
                                Array.from({ length: 5 }).map((_, i) => (
                                    <tr key={`skeleton-${i}`} className={styles.skeleton_row}>
                                        <td colSpan={6}>
                                            <div className={styles.skeleton} />
                                        </td>
                                    </tr>
                                ))}
                            {!isLoading && !!error && cards.length === 0 && (
                                <tr>
                                    <td colSpan={6}>
                                        <div className={styles.empty_state}>
                                            <div className={styles.empty_icon}>
                                                <Icon
                                                    name="error_outlined"
                                                    width={26}
                                                    height={26}
                                                />
                                            </div>
                                            <Typography variant="body3" weight="semibold">
                                                Failed to load gift cards
                                            </Typography>
                                            <Typography
                                                variant="body4"
                                                className={styles.empty_desc}>
                                                There was a problem fetching your gift cards.
                                            </Typography>
                                            <Button
                                                variant="outlined-primary"
                                                size="small"
                                                onClick={() => fetchCards({ isNew: true })}>
                                                Retry
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {!isLoading && !error && cards.length === 0 && (
                                <tr>
                                    <td colSpan={6}>
                                        <div className={styles.empty_state}>
                                            <div className={styles.empty_icon}>
                                                <Icon name="gift_card" width={26} height={26} />
                                            </div>
                                            <Typography variant="body3" weight="semibold">
                                                No gift cards yet
                                            </Typography>
                                            <Typography
                                                variant="body4"
                                                className={styles.empty_desc}>
                                                Issued gift cards will appear here.
                                            </Typography>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div ref={loader} />
        </motion.div>
    );
};

export default DevelopersGiftCardsLayout;
